import {
  EXERCISE_IMAGE_LIMIT,
  EXERCISE_IMAGE_MIME_TYPES,
  type AdminExerciseDetail,
} from '@steado/admin-contract/exercises';
import { useMutation } from '@tanstack/react-query';
import { useRef, useState } from 'react';

import { ConfirmDialog } from '@/components/shell/ConfirmDialog';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/lib/auth/auth-context';
import { cn } from '@/lib/utils';
import { Panel } from '@/components/panel/panels';

import {
  removeExerciseImage,
  removeExerciseMedia,
  uploadExerciseImage,
  uploadExerciseMedia,
} from '@/screens/exercises/exercises-query';

import { VideoPinPanel } from './VideoPinPanel';

interface MediaTabProps {
  readonly exercise: AdminExerciseDetail;
  readonly onMutated: (exercise: AdminExerciseDetail) => void;
}

/**
 * Removing a file is irreversible, but these sit in a panel header beside the
 * upload that is the panel's actual purpose. Outlined keeps the colour on the
 * label without making the header read as a warning.
 */
const DESTRUCTIVE_OUTLINE = 'border-err-fg/25 text-err-fg hover:bg-err-bg hover:text-err-fg';

/**
 * The demo clip, the imported stills, and Steado's pick of a YouTube video.
 * The only tab that writes files.
 */
export function MediaTab({ exercise, onMutated }: MediaTabProps) {
  const { api, can } = useAuth();
  const [isRemoving, setRemoving] = useState(false);
  const [removingImage, setRemovingImage] = useState<string | null>(null);
  const filePicker = useRef<HTMLInputElement>(null);
  const imagePicker = useRef<HTMLInputElement>(null);

  const uploadImage = useMutation({
    mutationFn: (file: File) => uploadExerciseImage(api, exercise.id, file),
    onSuccess: onMutated,
    retry: false,
  });

  const removeImage = useMutation({
    mutationFn: (key: string) => removeExerciseImage(api, exercise.id, key),
    onSuccess: (updated) => {
      setRemovingImage(null);
      onMutated(updated);
    },
    retry: false,
  });

  const uploadMedia = useMutation({
    mutationFn: (file: File) => uploadExerciseMedia(api, exercise.id, file),
    onSuccess: onMutated,
    retry: false,
  });

  const removeMedia = useMutation({
    mutationFn: () => removeExerciseMedia(api, exercise.id),
    onSuccess: (updated) => {
      setRemoving(false);
      onMutated(updated);
    },
    retry: false,
  });

  const isBusy =
    uploadMedia.isPending || removeMedia.isPending || uploadImage.isPending || removeImage.isPending;
  const disabledTitle = can.mutate ? undefined : 'Changing the library requires the admin role.';
  const isFull = exercise.images.length >= EXERCISE_IMAGE_LIMIT;

  return (
    <div className="flex flex-col gap-4">
      <Panel
        title="Demo clip"
        action={
          <div className="flex items-center gap-3">
            {/*
              A raw input rather than the console's `Input`: it is never drawn.
              The visible affordance is the button beside it, which forwards the
              click here.
            */}
            <input
              ref={filePicker}
              type="file"
              accept="video/mp4,video/webm"
              aria-label="Demo clip file"
              className="sr-only"
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file !== undefined) uploadMedia.mutate(file);
                // Cleared so picking the same file twice still fires a change
                // event — a re-upload after a failure must work.
                event.target.value = '';
              }}
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                filePicker.current?.click();
              }}
              disabled={!can.mutate || isBusy}
              title={disabledTitle}
            >
              {uploadMedia.isPending
                ? 'Uploading…'
                : exercise.media === null
                  ? 'Upload clip'
                  : 'Replace clip'}
            </Button>
            {exercise.media === null ? null : (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  setRemoving(true);
                }}
                disabled={!can.mutate || isBusy}
                title={disabledTitle}
                className={DESTRUCTIVE_OUTLINE}
              >
                Remove
              </Button>
            )}
          </div>
        }
      >
        {exercise.media === null ? (
          <p className="text-sm text-muted-foreground">
            No clip. The app shows the written cues alone until one is uploaded.
          </p>
        ) : (
          <div className="flex flex-col gap-3">
            {exercise.media.url === null ? (
              <p className="text-[13px] leading-relaxed text-muted-foreground">
                {/* Key present, no url: a clip exists but this environment has
                    no public endpoint to play it from. */}
                A clip is attached, but this environment has no public storage endpoint to play it from.
              </p>
            ) : (
              <video
                src={exercise.media.url}
                controls
                className="w-full max-w-[520px] rounded-xl border border-border bg-foreground"
              />
            )}
            <p className="font-mono text-xs break-all text-muted-foreground">{exercise.media.key}</p>
          </div>
        )}

        {uploadMedia.isError ? (
          <p role="alert" className="mt-3 text-[13px] text-err-fg">
            {uploadMedia.error.message}
          </p>
        ) : null}
      </Panel>

      <Panel
        title={`Demonstration stills (${String(exercise.images.length)}/${String(EXERCISE_IMAGE_LIMIT)})`}
        action={
          <div className="flex items-center gap-3">
            <input
              ref={imagePicker}
              type="file"
              accept={EXERCISE_IMAGE_MIME_TYPES.join(',')}
              aria-label="Demonstration still file"
              className="sr-only"
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file !== undefined) uploadImage.mutate(file);
                event.target.value = '';
              }}
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                imagePicker.current?.click();
              }}
              disabled={!can.mutate || isBusy || isFull}
              title={
                isFull
                  ? `Remove one first; an exercise carries at most ${String(EXERCISE_IMAGE_LIMIT)} stills.`
                  : disabledTitle
              }
            >
              {uploadImage.isPending ? 'Uploading…' : 'Add still'}
            </Button>
          </div>
        }
      >
        {exercise.images.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No stills. Hand-written entries have none; an imported row without them means the media pass has
            not run, or could not fetch them.
          </p>
        ) : (
          <div className="grid grid-cols-[repeat(auto-fill,minmax(220px,1fr))] gap-3">
            {exercise.images.map((image) => (
              <figure key={image.key} className="flex flex-col gap-2">
                {image.url === null ? (
                  // Key without a url: the file is mirrored, this environment
                  // just has no public endpoint to serve it.
                  <p className="rounded-xl border border-border bg-muted px-3 py-6 text-center text-xs text-muted-foreground">
                    No endpoint here to serve this
                  </p>
                ) : (
                  <img
                    src={image.url}
                    alt={`${exercise.name} demonstration`}
                    className="w-full rounded-xl border border-border bg-muted"
                  />
                )}
                <figcaption className="flex items-start justify-between gap-2">
                  <span className="min-w-0 font-mono text-xs break-all text-muted-foreground">
                    {image.key}
                  </span>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setRemovingImage(image.key);
                    }}
                    disabled={!can.mutate || isBusy}
                    title={disabledTitle}
                    aria-label={`Remove still ${image.key}`}
                    className={cn('h-7 px-2.5 text-xs', DESTRUCTIVE_OUTLINE)}
                  >
                    Remove
                  </Button>
                </figcaption>
              </figure>
            ))}
          </div>
        )}

        {uploadImage.isError ? (
          <p role="alert" className="mt-3 text-[13px] text-err-fg">
            {uploadImage.error.message}
          </p>
        ) : null}
      </Panel>

      <VideoPinPanel exerciseId={exercise.id} />

      {isRemoving ? (
        <ConfirmDialog
          title="Remove the demo clip?"
          body={
            <p>
              The file is deleted from storage and does not come back. The key is recorded in the audit log,
              so what went stays knowable. The exercise itself is untouched.
            </p>
          }
          confirmLabel="Remove clip"
          destructive
          isPending={removeMedia.isPending}
          {...(removeMedia.isError ? { error: removeMedia.error.message } : {})}
          onConfirm={() => {
            removeMedia.mutate();
          }}
          onCancel={() => {
            setRemoving(false);
          }}
        />
      ) : null}

      {removingImage === null ? null : (
        <ConfirmDialog
          title="Remove this still?"
          body={
            <p>
              The file is deleted from storage and does not come back. The key is recorded in the audit log.
              An imported still cannot be re-fetched from here; the media pass would have to run again.
            </p>
          }
          confirmLabel="Remove still"
          destructive
          isPending={removeImage.isPending}
          {...(removeImage.isError ? { error: removeImage.error.message } : {})}
          onConfirm={() => {
            removeImage.mutate(removingImage);
          }}
          onCancel={() => {
            setRemovingImage(null);
          }}
        />
      )}
    </div>
  );
}
